<template>
  <div class="filter-wrapper">
    <div class="filter-card">
      <div class="row row--top">
        <div class="field field--grow search-field">
          <div class="label">アカウント名検索</div>
          <el-input v-model="form.keyword" placeholder="検索(完全一致)" clearable>
            <template slot="prepend"><i class="mdi mdi-magnify"></i></template>
          </el-input>
        </div>
        <div class="field field--status">
          <select-form
            class="select-form"
            label="応募状況"
            :value="form.status"
            :options="statusOptions"
            :onChange="handleStatusChange"
            clearable
          />
        </div>
      </div>

      <div class="row row--date">
        <div class="field field--date">
          <div class="label">応募期間</div>
          <AppDateRangePicker
            :value="[form.entryStart, form.entryEnd]"
            :placeholder="['開始日時', '終了日時']"
            :campaign="campaign"
            @change="onEntryChange"
          />
        </div>
      </div>

      <div class="row row--ranges">
        <InputRangeForm
          class="range-field"
          label="応募数"
          :value="[form.applicantCountMin, form.applicantCountMax]"
          @input="onApplicantCountChange"
        />
        <InputRangeForm
          class="range-field"
          label="累計参加数"
          :value="[form.totalEntryCountMin, form.totalEntryCountMax]"
          @input="onTotalEntryChange"
        />
        <InputRangeForm
          class="range-field"
          label="累計当選数"
          :value="[form.totalWinningCountMin, form.totalWinningCountMax]"
          @input="onTotalWinningChange"
        />
      </div>

      <div layout layout-align="start-center" class="action-row">
        <el-button type="primary" class="search-main-button" @click="handleSearch" :loading="loading"
          ><i class="mdi mdi-plus"></i>詳細条件を追加して検索</el-button
        >
      </div>

      <div v-if="errors.length" class="errors">
        <div v-for="(error, index) in errors" :key="index" class="error-text">
          {{ error }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { defineComponent } from 'vue'

import AppDateRangePicker from '@/client/components/_atoms/app_date_range_picker'
import InputRangeForm from '@/client/components/_atoms/input_range_form'
import SelectForm from '@/client/components/_atoms/select_form'

import { ApplicantStatus } from '../api/getInstagramInstantwinApplicants'
import { ApplicantSearchFilters } from '../hooks/instagramInstantwinApplicantStore'

type RangeValue = [number | string | undefined, number | string | undefined]
type StatusOption = { label: string; value: ApplicantStatus | null }

type FilterFormState = {
  keyword: string
  status: ApplicantStatus | null
  detailsOpen: boolean
  entryStart: string
  entryEnd: string
  applicantCountMin?: number | string
  applicantCountMax?: number | string
  totalEntryCountMin?: number | string
  totalEntryCountMax?: number | string
  totalWinningCountMin?: number | string
  totalWinningCountMax?: number | string
}

export default defineComponent({
  name: 'InstagramInstantwinApplicantFilter',
  components: {
    AppDateRangePicker,
    InputRangeForm,
    SelectForm
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue'
  },
  props: {
    modelValue: {
      type: Object as () => ApplicantSearchFilters,
      default: () => ({ detailsOpen: true })
    },
    defaultEntryStart: {
      type: String,
      default: ''
    },
    defaultEntryEnd: {
      type: String,
      default: ''
    },
    campaign: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: this.createForm(this.modelValue),
      errors: [] as string[],
      statusOptions: [
        { label: '指定なし', value: null },
        { label: '抽選前', value: 'PROCESSING' },
        { label: '当選済', value: 'WIN' },
        { label: '離脱済', value: 'MISSING' },
        { label: '落選済', value: 'LOSE' }
      ] as StatusOption[]
    }
  },
  watch: {
    modelValue: {
      deep: true,
      handler(newVal: ApplicantSearchFilters) {
        this.form = this.createForm(newVal)
      }
    }
  },
  methods: {
    createForm(filters: ApplicantSearchFilters): FilterFormState {
      return {
        keyword: filters.keyword ?? '',
        status: filters.status && filters.status.length > 0 ? filters.status[0] : null,
        detailsOpen: filters.detailsOpen ?? false,
        entryStart: filters.entry?.start ?? this.defaultEntryStart ?? '',
        entryEnd: filters.entry?.end ?? this.defaultEntryEnd ?? '',
        applicantCountMin: filters.applicantCount?.min,
        applicantCountMax: filters.applicantCount?.max,
        totalEntryCountMin: filters.totalEntryCount?.min,
        totalEntryCountMax: filters.totalEntryCount?.max,
        totalWinningCountMin: filters.totalWinningCount?.min,
        totalWinningCountMax: filters.totalWinningCount?.max
      }
    },
    normalizeKeyword(value: string): string | undefined {
      const trimmed = value.trim()
      if (!trimmed) return undefined
      return trimmed.replace(/^@/, '')
    },
    toNumber(value: number | string | undefined): number | undefined {
      if (typeof value === 'number') return value
      if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value)
        return Number.isNaN(parsed) ? undefined : parsed
      }
      return undefined
    },
    buildRange(min?: number | string, max?: number | string) {
      const parsedMin = this.toNumber(min)
      const parsedMax = this.toNumber(max)
      if (typeof parsedMin === 'undefined' && typeof parsedMax === 'undefined') return undefined
      return { min: parsedMin, max: parsedMax }
    },
    buildFilters(): ApplicantSearchFilters {
      const keyword = this.normalizeKeyword(this.form.keyword)
      const status = this.form.status ? [this.form.status] : undefined

      const filters: ApplicantSearchFilters = {
        keyword,
        status,
        detailsOpen: this.form.detailsOpen
      }

      if (this.form.detailsOpen) {
        const entry = this.buildEntryRange()
        const applicantCount = this.buildRange(this.form.applicantCountMin, this.form.applicantCountMax)
        const totalEntryCount = this.buildRange(this.form.totalEntryCountMin, this.form.totalEntryCountMax)
        const totalWinningCount = this.buildRange(this.form.totalWinningCountMin, this.form.totalWinningCountMax)
        if (entry) filters.entry = entry
        if (applicantCount) filters.applicantCount = applicantCount
        if (totalEntryCount) filters.totalEntryCount = totalEntryCount
        if (totalWinningCount) filters.totalWinningCount = totalWinningCount
      }

      return filters
    },
    buildEntryRange() {
      const start = this.form.entryStart && this.form.entryStart.trim().length > 0 ? this.form.entryStart : undefined
      const end = this.form.entryEnd && this.form.entryEnd.trim().length > 0 ? this.form.entryEnd : undefined
      if (!start && !end) return undefined
      return { start, end }
    },
    validateRanges(): string[] {
      const errors: string[] = []
      if (!this.form.detailsOpen) {
        return errors
      }

      const applicantRange = this.buildRange(this.form.applicantCountMin, this.form.applicantCountMax)
      if (
        applicantRange &&
        typeof applicantRange.min !== 'undefined' &&
        typeof applicantRange.max !== 'undefined' &&
        applicantRange.min > applicantRange.max
      ) {
        errors.push('応募数の下限と上限が逆転しています')
      }

      const totalEntryRange = this.buildRange(this.form.totalEntryCountMin, this.form.totalEntryCountMax)
      if (
        totalEntryRange &&
        typeof totalEntryRange.min !== 'undefined' &&
        typeof totalEntryRange.max !== 'undefined' &&
        totalEntryRange.min > totalEntryRange.max
      ) {
        errors.push('累計参加数の下限と上限が逆転しています')
      }

      const totalWinningRange = this.buildRange(this.form.totalWinningCountMin, this.form.totalWinningCountMax)
      if (
        totalWinningRange &&
        typeof totalWinningRange.min !== 'undefined' &&
        typeof totalWinningRange.max !== 'undefined' &&
        totalWinningRange.min > totalWinningRange.max
      ) {
        errors.push('累計当選数の下限と上限が逆転しています')
      }

      return errors
    },
    handleSearch() {
      this.errors = this.validateRanges()
      if (this.errors.length > 0) return
      const filters = this.buildFilters()
      this.$emit('update:modelValue', filters)
      this.$emit('search', filters)
    },
    handleReset() {
      this.errors = []
      const resetFilters: ApplicantSearchFilters = { detailsOpen: false }
      this.form = this.createForm(resetFilters)
      this.$emit('update:modelValue', resetFilters)
      this.$emit('reset')
    },
    handleStatusChange(value: ApplicantStatus | ApplicantStatus[] | null | undefined) {
      if (Array.isArray(value)) {
        this.form.status = value[0] ?? null
      } else if (value) {
        this.form.status = value
      } else {
        this.form.status = null
      }
    },
    onEntryChange(value: string[]) {
      const [start, end] = value
      this.form.entryStart = start || ''
      this.form.entryEnd = end || ''
    },
    onApplicantCountChange(value: RangeValue) {
      ;[this.form.applicantCountMin, this.form.applicantCountMax] = value
    },
    onTotalEntryChange(value: RangeValue) {
      ;[this.form.totalEntryCountMin, this.form.totalEntryCountMax] = value
    },
    onTotalWinningChange(value: RangeValue) {
      ;[this.form.totalWinningCountMin, this.form.totalWinningCountMax] = value
    },
    toggleDetails() {
      this.form.detailsOpen = !this.form.detailsOpen
      if (!this.form.detailsOpen) {
        this.form.entryStart = ''
        this.form.entryEnd = ''
        this.form.applicantCountMin = undefined
        this.form.applicantCountMax = undefined
        this.form.totalEntryCountMin = undefined
        this.form.totalEntryCountMax = undefined
        this.form.totalWinningCountMin = undefined
        this.form.totalWinningCountMax = undefined
      }
    }
  }
})
</script>

<style scoped lang="scss">
.filter-wrapper {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto 16px;
}

.filter-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 16px;
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 6px;
}

.row {
  display: flex;
  gap: 12px;
  width: 100%;
}

.row--top {
  align-items: flex-end;
}

.row--date {
  align-items: center;
}

.row--ranges {
  flex-wrap: wrap;
  gap: 8px 4px;
  align-items: center;
  margin-top: 0;
}

.field--grow {
  width: 100%;
}

.search-field {
  width: 320px;
}

.field--status {
  width: 200px;
  min-width: 180px;
}

.field--status :deep(.select-form) {
  width: 100%;
}

.field--status :deep(.el-input),
.field--status :deep(.el-input__inner) {
  width: 100%;
  height: 32px;
}

.field--date {
  width: 100%;
  max-width: 560px;
}

.field--date :deep(.app-date-range-picker) {
  width: 100%;
}

.field--date :deep(.el-input__inner) {
  height: 32px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.label {
  color: #3f434a;
}

.field :deep(.el-input__inner) {
  width: 100%;
  height: 32px;
}

.range-field {
  flex: 0 0 auto;
}

.range-field :deep(.el-input__inner) {
  width: 100%;
  height: 32px;
}

.action-row {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-start;
  padding: 0;
  margin-top: 4px;
}

.search-main-button {
  height: 32px;
  color: #fff;
  white-space: nowrap;
  background: #e85f8c;
  border-color: #e85f8c;
}

.search-main-button:hover,
.search-main-button:focus {
  color: #fff;
  background: #d85280;
  border-color: #d85280;
}

.errors {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.error-text {
  font-size: 12px;
  color: #d0021b;
}
</style>
